import type { TypedAmountSpec } from "../typedAmountTypes";

/**
 * Definition of Earnings typed-amount spec. The composite salary types carry an
 * average-month input; the fixed-point types (base salary, prior-year W-2, K-1)
 * and the sentinels hide it. Shared by every LOC that carries the field (STD,
 * LTD, Basic Life, Voluntary Life).
 */
export const definitionOfEarningsSpec: TypedAmountSpec = {
  typeKey: "definitionOfEarningsType",
  amountKey: "definitionOfEarningsAverageMonths",
  amountLabel: "Average Months",
  amountMin: 0,
  amountInteger: true,
  amountOptional: true,
  typeOptions: [
    { value: "salary", label: "Base Salary", hideAmount: true },
    { value: "salaryPlusCommissions", label: "Salary + Comm" },
    { value: "salaryPlusBonus", label: "Salary + Bonus" },
    {
      value: "salaryPlusBonusPlusCommissions",
      label: "Salary + Bonus + Comm",
    },
    { value: "priorYearW2", label: "Prior Year W2", hideAmount: true },
    { value: "k1", label: "K1", hideAmount: true },
    { value: "notApplicable", label: "Not Applicable", hideAmount: true },
    { value: "notIncluded", label: "Not Included", hideAmount: true },
    { value: "notDisclosed", label: "Not Disclosed", hideAmount: true },
  ],
};

/**
 * Maximum Amount typed-amount spec. STD and LTD share an identical
 * `MaximumAmountType` enum (dollarAmount / dollarAmountNotToExceedPercentage /
 * unlimited / notDisclosed), so both registries reuse this single spec. The
 * percentage input is an extra sibling shown only for the "not to exceed
 * percentage" type.
 */
export const maximumAmountSpec: TypedAmountSpec = {
  typeKey: "maximumAmountType",
  amountKey: "maximumAmountDollarAmount",
  amountLabel: "Dollar",
  amountMin: 0,
  typeOptions: [
    { value: "dollarAmount", label: "Dollar Amount" },
    {
      value: "dollarAmountNotToExceedPercentageOfPredisabilityEarnings",
      label: "Dollar Amount Not To Exceed Percentage",
    },
    { value: "unlimited", label: "Unlimited", hideAmount: true },
    { value: "notDisclosed", label: "Not Disclosed", hideAmount: true },
  ],
  extraKeys: [
    {
      key: "maximumAmountPercent",
      label: "Percentage",
      showForTypes: [
        "dollarAmountNotToExceedPercentageOfPredisabilityEarnings",
      ],
      min: 0,
      max: 100,
    },
  ],
};

export const rateGuarantee: TypedAmountSpec = {
  typeKey: "rateGuaranteeType",
  amountKey: "rateGuaranteeAmount",
  amountLabel: "Amount",
  typeOptions: [
    { value: "months", label: "Months" },
    { value: "years", label: "Years" },
    { value: "notIncluded", label: "Not Included", hideAmount: true },
    { value: "notOffered", label: "Not Offered", hideAmount: true },
  ],
  amountMin: 1,
  amountInteger: true,
};
